import { useState, useEffect, useCallback, useRef } from 'react';
import { posthog } from './posthog';
import { Player } from './Player';
import { StatusPoller, type AudioStatusResponse } from './StatusPoller';
import { Subscribe } from './Subscribe';

// ============================================================================
// Types
// ============================================================================

interface ContentInfo {
  id: string;
  title: string;
  author: string | null;
  siteName: string | null;
  wordCount: number;
}

interface AudioInfo {
  id: string;
  status: 'pending' | 'processing' | 'ready' | 'failed';
  progress?: number;
  estimatedSeconds?: number;
  audioUrl?: string;
  durationSeconds?: number;
  errorMessage?: string;
}

interface QueueItemStatus {
  success: boolean;
  queueItemId: string;
  content: ContentInfo;
  audio: AudioInfo;
}

// ============================================================================
// Configuration
// ============================================================================

const API_URL = import.meta.env.VITE_API_URL || 'https://api.earpocket.com';
const FEEDS_URL = import.meta.env.VITE_FEEDS_URL || 'https://feeds.earpocket.com';
const PLAYER_URL = import.meta.env.VITE_PLAYER_URL || 'https://play.earpocket.com';

// ============================================================================
// Brand Icons (SVG)
// ============================================================================

const IconWarning = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/>
    <line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);

const IconSmartphone = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
    <line x1="12" y1="18" x2="12.01" y2="18"/>
  </svg>
);

// ============================================================================
// Route Detection
// ============================================================================

type Route = 
  | { type: 'subscribe'; token: string; queueItemId?: string }
  | { type: 'player'; queueItemId: string }
  | { type: 'home' };

function parseRoute(): Route {
  const pathParts = window.location.pathname.split('/').filter(Boolean);
  
  // /subscribe/{token} - QR code landing page
  if (pathParts[0] === 'subscribe' && pathParts[1]) {
    const params = new URLSearchParams(window.location.search);
    const queueItemId = params.get('play') || undefined;
    return { type: 'subscribe', token: pathParts[1], queueItemId };
  }
  
  // /{queueItemId} - audio player
  if (pathParts[0] && pathParts[0].length > 0) {
    return { type: 'player', queueItemId: pathParts[0] };
  }
  
  // / - home page (no ID)
  return { type: 'home' };
}

// ============================================================================
// App Component
// ============================================================================

export default function App() {
  const [route] = useState<Route>(() => parseRoute());
  
  // Handle subscribe route
  if (route.type === 'subscribe') {
    return (
      <Subscribe 
        token={route.token} 
        feedsUrl={FEEDS_URL} 
        playerUrl={PLAYER_URL}
        apiUrl={API_URL}
        queueItemId={route.queueItemId}
      />
    );
  }
  
  // Handle home route (no queue item ID)
  if (route.type === 'home') {
    return (
      <div className="container">
        <div className="player-card">
          <div className="header">
            <div className="logo">Earpocket</div>
            <h1 className="article-title">Welcome to Earpocket</h1>
            <p className="article-meta">Turn any article into a podcast episode</p>
          </div>
          <div className="player-content">
            <p className="status-text">Get started with the Earpocket browser extension.</p>
            <p className="status-subtext">
              Available for{' '}
              <a href="https://earpocket.app" target="_blank" rel="noopener noreferrer" style={{ color: '#0ea5e9' }}>
                Chrome and Firefox
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }
  
  // Player route - existing logic below
  return <PlayerView queueItemId={route.queueItemId} />;
}

// ============================================================================
// Player View Component
// ============================================================================

function PlayerView({ queueItemId }: { queueItemId: string }) {
  const [status, setStatus] = useState<QueueItemStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const trackedOpen = useRef(false);

  // Fetch initial status
  useEffect(() => {
    if (!queueItemId) return;

    fetchStatus(queueItemId);
  }, [queueItemId]);

  // Track player.opened once status is loaded
  useEffect(() => {
    if (status && !trackedOpen.current) {
      trackedOpen.current = true;
      posthog.capture('player.opened', {
        queueItemId,
        title: status.content?.title,
        wordCount: status.content?.wordCount,
      });
    }
  }, [status, queueItemId]);

  async function fetchStatus(id: string) {
    try {
      const response = await fetch(`${API_URL}/v1/audio/queue/${id}/status`);
      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error?.message || 'Failed to load audio');
      }

      setStatus(data);
      setLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load audio');
      setLoading(false);
    }
  }

  const handleStatusUpdate = useCallback((audioStatus: AudioStatusResponse) => {
    // Merge the audio update with existing status
    setStatus((prevStatus) => {
      if (!prevStatus) return prevStatus;
      return {
        ...prevStatus,
        audio: {
          ...prevStatus.audio,
          status: audioStatus.audio.status,
          progress: audioStatus.audio.progress ?? prevStatus.audio.progress,
          estimatedSeconds: audioStatus.audio.estimatedSeconds ?? prevStatus.audio.estimatedSeconds,
          audioUrl: audioStatus.audio.audioUrl ?? prevStatus.audio.audioUrl,
          durationSeconds: audioStatus.audio.durationSeconds ?? prevStatus.audio.durationSeconds,
          errorMessage: audioStatus.audio.errorMessage ?? prevStatus.audio.errorMessage,
        },
      };
    });
  }, []);

  function handleRetry() {
    if (queueItemId) {
      setError(null);
      setLoading(true);
      fetchStatus(queueItemId);
    }
  }

  // Get user token from localStorage for feed URL
  const userToken = typeof window !== 'undefined' 
    ? localStorage.getItem('earpocket_token') 
    : null;
  const feedUrl = userToken ? `${FEEDS_URL}/${userToken}.xml` : null;

  return (
    <div className="container">
      <div className="player-card">
        <div className="header">
          <div className="logo">Earpocket</div>
          {status?.content && (
            <>
              <h1 className="article-title">{status.content.title}</h1>
              <div className="article-meta">
                {status.content.author && <span>By {status.content.author}</span>}
                {status.content.author && status.content.siteName && ' · '}
                {status.content.siteName && <span>{status.content.siteName}</span>}
              </div>
            </>
          )}
          {loading && !status && (
            <>
              <h1 className="article-title">Loading...</h1>
            </>
          )}
        </div>

        <div className="player-content">
          {loading && (
            <div className="status-container">
              <div className="spinner" />
              <p className="status-text">Loading audio...</p>
            </div>
          )}

          {error && (
            <div className="error-container">
              <div className="error-icon">
                <IconWarning />
              </div>
              <p className="error-message">{error}</p>
              <button className="retry-button" onClick={handleRetry}>
                Try Again
              </button>
            </div>
          )}

          {!loading && !error && status && (
            <>
              {status.audio.status === 'ready' && status.audio.audioUrl ? (
                <Player
                  audioUrl={status.audio.audioUrl.startsWith('http') ? status.audio.audioUrl : `${API_URL}/${status.audio.audioUrl}`}
                  durationSeconds={status.audio.durationSeconds || 0}
                  queueItemId={queueItemId}
                  title={status.content?.title}
                />
              ) : status.audio.status === 'failed' ? (
                <div className="error-container">
                  <div className="error-icon">
                    <IconWarning />
                  </div>
                  <p className="error-message">
                    {status.audio.errorMessage || 'Audio generation failed'}
                  </p>
                  <button className="retry-button" onClick={handleRetry}>
                    Try Again
                  </button>
                </div>
              ) : (
                <StatusPoller
                  queueItemId={queueItemId!}
                  apiUrl={API_URL}
                  initialProgress={status.audio.progress || 0}
                  estimatedSeconds={status.audio.estimatedSeconds}
                  onStatusUpdate={handleStatusUpdate}
                />
              )}
            </>
          )}
        </div>

        {feedUrl && (
          <div className="footer">
            <p className="podcast-cta">Want this in your podcast app?</p>
            <a
              href={`podcast://${feedUrl.replace('https://', '')}`}
              className="podcast-button"
            >
              <IconSmartphone />
              Add to Podcast App
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
